import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Paper,
  Chip,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Tooltip,
  Button,
  FormControlLabel,
  Switch,
  Collapse,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Clear as ClearIcon,
  ContentCopy as CopyIcon,
  Help as HelpIcon,
  VisibilityOff as VisibilityOffIcon,
  Speed as SpeedIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';

interface MatchResult {
  match: string;
  index: number;
  groups?: string[];
}

const RegexPlayground: React.FC = () => {
  const [pattern, setPattern] = useState('');
  const [testString, setTestString] = useState('');
  const [flags, setFlags] = useState('g');
  const [matches, setMatches] = useState<MatchResult[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isValid, setIsValid] = useState(true);
  const [showReference, setShowReference] = useState(true);
  const [showExplanation] = useState(false);
  const [highlightMode, setHighlightMode] = useState<'basic' | 'groups'>('basic');
  const [testMode, setTestMode] = useState<'match' | 'replace'>('match');
  const [replaceString, setReplaceString] = useState('');
  const [replaceResult, setReplaceResult] = useState('');
  const [patternComplexity, setPatternComplexity] = useState<'low' | 'medium' | 'high'>('low');

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Ctrl/Cmd + K: Clear all
      if ((event.ctrlKey || event.metaKey) && event.key === 'k') {
        event.preventDefault();
        setPattern('');
        setTestString('');
        setReplaceString('');
      }

      // Ctrl/Cmd + Enter: Focus test string (or trigger test)
      if ((event.ctrlKey || event.metaKey) && event.key === 'Enter') {
        event.preventDefault();
        // Focus test string input if pattern exists
        const testStringInput = document.querySelector(
          'textarea[placeholder*="Enter text here"]'
        ) as HTMLTextAreaElement;
        if (testStringInput) {
          testStringInput.focus();
        }
      }

      // Ctrl/Cmd + 1-6: Quick pattern insertion
      if ((event.ctrlKey || event.metaKey) && event.key >= '1' && event.key <= '6') {
        event.preventDefault();
        const quickPatterns = [
          '\\d+',
          '\\w+',
          '\\s+',
          '[a-zA-Z]+',
          '\\w+@\\w+\\.\\w+',
          '\\d{4}-\\d{2}-\\d{2}',
        ];
        const patternIndex = parseInt(event.key) - 1;
        if (quickPatterns[patternIndex]) {
          setPattern(quickPatterns[patternIndex]);
        }
      }

      // Ctrl/Cmd + R: Toggle between match and replace mode
      if ((event.ctrlKey || event.metaKey) && event.key === 'r') {
        event.preventDefault();
        setTestMode(prev => (prev === 'match' ? 'replace' : 'match'));
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Sample patterns for quick testing
  const samplePatterns = [
    { name: 'Email', pattern: '\\w+@\\w+\\.\\w+', description: 'Basic email pattern' },
    {
      name: 'Phone',
      pattern: '\\d{3}-\\d{3}-\\d{4}',
      description: 'US phone number (XXX-XXX-XXXX)',
    },
    { name: 'URL', pattern: 'https?://[\\w.-]+\\.[a-z]{2,}', description: 'Basic URL pattern' },
    { name: 'Date', pattern: '\\d{4}-\\d{2}-\\d{2}', description: 'Date in YYYY-MM-DD format' },
    { name: 'Word', pattern: '\\b\\w+\\b', description: 'Match whole words' },
    { name: 'Digits', pattern: '\\d+', description: 'Match sequences of digits' },
  ];

  // Sample test strings
  const sampleStrings = [
    'Contact us at support@example.com or call 555-123-4567',
    'Visit https://www.example.com for more info',
    'Important dates: 2024-01-15, 2024-02-20, 2024-03-30',
    'The quick brown fox jumps over the lazy dog',
    'Phone numbers: 123-456-7890, 555-987-6543, 800-555-1234',
    'Mixed content: user@domain.org, phone: 555-123-4567, date: 2024-01-01',
  ];

  // Regex reference
  const regexReference = [
    {
      category: 'Character Classes',
      items: [
        { pattern: '.', description: 'Any character except newline' },
        { pattern: '\\d', description: 'Any digit [0-9]' },
        { pattern: '\\w', description: 'Any word character [a-zA-Z0-9_]' },
        { pattern: '\\s', description: 'Any whitespace character' },
        { pattern: '[abc]', description: 'Any character in the set' },
        { pattern: '[^abc]', description: 'Any character NOT in the set' },
        { pattern: '[a-z]', description: 'Any character in the range' },
      ],
    },
    {
      category: 'Quantifiers',
      items: [
        { pattern: '*', description: 'Zero or more' },
        { pattern: '+', description: 'One or more' },
        { pattern: '?', description: 'Zero or one (optional)' },
        { pattern: '{n}', description: 'Exactly n times' },
        { pattern: '{n,}', description: 'n or more times' },
        { pattern: '{n,m}', description: 'Between n and m times' },
      ],
    },
    {
      category: 'Anchors',
      items: [
        { pattern: '^', description: 'Start of string' },
        { pattern: '$', description: 'End of string' },
        { pattern: '\\b', description: 'Word boundary' },
        { pattern: '\\B', description: 'Not a word boundary' },
      ],
    },
    {
      category: 'Groups & Alternation',
      items: [
        { pattern: '(abc)', description: 'Capturing group' },
        { pattern: '(?:abc)', description: 'Non-capturing group' },
        { pattern: 'a|b', description: 'Alternation (a OR b)' },
      ],
    },
  ];

  // Pattern complexity analysis
  const analyzePatternComplexity = useCallback((pattern: string) => {
    if (!pattern) return 'low';

    const complexFeatures = [
      /\([^)]*\)/g, // Groups
      /\[\^?[^\]]*\]/g, // Character classes
      /[*+?{]/g, // Quantifiers
      /\\[dwsWDS]/g, // Character class shortcuts
      /\$|\^/g, // Anchors
    ];

    let complexity = 0;
    complexFeatures.forEach(feature => {
      const matches = pattern.match(feature);
      if (matches) complexity += matches.length;
    });

    if (complexity <= 2) return 'low';
    if (complexity <= 5) return 'medium';
    return 'high';
  }, []);

  // Pattern explanation generator
  const generatePatternExplanation = useCallback((pattern: string) => {
    if (!pattern) return 'Enter a pattern to see explanation';

    const explanations: string[] = [];

    // Basic character explanations
    if (pattern.includes('\\d')) explanations.push('\\d matches any digit (0-9)');
    if (pattern.includes('\\w'))
      explanations.push('\\w matches any word character (letters, digits, underscore)');
    if (pattern.includes('\\s')) explanations.push('\\s matches any whitespace character');
    if (pattern.includes('.')) explanations.push('. matches any character except newline');
    if (pattern.includes('^')) explanations.push('^ anchors to start of string');
    if (pattern.includes('$')) explanations.push('$ anchors to end of string');
    if (pattern.includes('+')) explanations.push('+ matches one or more of the preceding element');
    if (pattern.includes('*')) explanations.push('* matches zero or more of the preceding element');
    if (pattern.includes('?')) explanations.push('? makes the preceding element optional');

    return explanations.length > 0
      ? explanations.join('; ')
      : 'Pattern uses literal character matching';
  }, []);

  useEffect(() => {
    if (!pattern.trim()) {
      setMatches([]);
      setError(null);
      setIsValid(true);
      setReplaceResult('');
      setPatternComplexity('low');
      return;
    }

    // Analyze pattern complexity
    setPatternComplexity(analyzePatternComplexity(pattern));

    try {
      const regex = new RegExp(pattern, flags);
      setError(null);
      setIsValid(true);

      if (!testString.trim()) {
        setMatches([]);
        setReplaceResult('');
        return;
      }

      // Handle replace mode
      if (testMode === 'replace') {
        try {
          const result = testString.replace(regex, replaceString);
          setReplaceResult(result);
        } catch {
          setReplaceResult('Error in replacement');
        }
      }

      const foundMatches: MatchResult[] = [];
      let match;

      if (flags.includes('g')) {
        // Reset regex for global matching
        const globalRegex = new RegExp(pattern, flags);
        while ((match = globalRegex.exec(testString)) !== null) {
          foundMatches.push({
            match: match[0],
            index: match.index,
            groups: match.slice(1),
          });
          if (match.index === globalRegex.lastIndex) {
            break; // Avoid infinite loop
          }
        }
      } else {
        match = regex.exec(testString);
        if (match) {
          foundMatches.push({
            match: match[0],
            index: match.index,
            groups: match.slice(1),
          });
        }
      }

      setMatches(foundMatches);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Invalid regex pattern');
      setIsValid(false);
      setMatches([]);
      setReplaceResult('');
    }
  }, [pattern, testString, flags, testMode, replaceString, analyzePatternComplexity]);

  const handleSamplePatternClick = (samplePattern: string) => {
    setPattern(samplePattern);
  };

  const handleSampleStringClick = (sampleString: string) => {
    setTestString(sampleString);
  };

  const getGroupColors = () => [
    '#ffeb3b',
    '#f44336',
    '#4caf50',
    '#2196f3',
    '#ff9800',
    '#9c27b0',
    '#00bcd4',
    '#795548',
    '#607d8b',
    '#3f51b5',
  ];

  const highlightMatches = (text: string, matchResults: MatchResult[]) => {
    if (matchResults.length === 0) return text;

    let result = '';
    let lastIndex = 0;
    const colors = getGroupColors();

    matchResults.forEach((match, matchIndex) => {
      // Add text before match
      result += text.slice(lastIndex, match.index);

      if (highlightMode === 'groups' && match.groups && match.groups.length > 0) {
        // Highlight each capture group with different colors
        const matchText = match.match;

        // This is simplified - for full group highlighting, we'd need more complex parsing
        result += `<mark style="background-color: ${colors[0]}; color: black; padding: 1px 2px; border-radius: 2px; border: 2px solid #333;">${matchText}</mark>`;
      } else {
        // Basic highlighting
        const color = highlightMode === 'basic' ? '#ffeb3b' : colors[matchIndex % colors.length];
        result += `<mark style="background-color: ${color}; color: black; padding: 1px 2px; border-radius: 2px;">${match.match}</mark>`;
      }

      lastIndex = match.index + match.match.length;
    });

    // Add remaining text
    result += text.slice(lastIndex);
    return result;
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  return (
    <Container maxWidth='xl' sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant='h3' component='h1'>
          Regex Playground
        </Typography>

        {/* Toolbar */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <ToggleButtonGroup
            value={testMode}
            exclusive
            onChange={(_e, value) => value && setTestMode(value)}
            size='small'
          >
            <ToggleButton value='match'>Match</ToggleButton>
            <ToggleButton value='replace'>Replace</ToggleButton>
          </ToggleButtonGroup>

          <ToggleButtonGroup
            value={highlightMode}
            exclusive
            onChange={(_e, value) => value && setHighlightMode(value)}
            size='small'
          >
            <ToggleButton value='basic'>Basic</ToggleButton>
            <ToggleButton value='groups'>Groups</ToggleButton>
          </ToggleButtonGroup>

          <FormControlLabel
            control={
              <Switch
                checked={showReference}
                onChange={e => setShowReference(e.target.checked)}
                size='small'
              />
            }
            label='Reference'
          />

          <Tooltip
            title={
              <Box>
                <Typography variant='caption' display='block'>
                  <strong>Keyboard Shortcuts:</strong>
                </Typography>
                <Typography variant='caption' display='block'>
                  Ctrl/⌘ + K: Clear all
                </Typography>
                <Typography variant='caption' display='block'>
                  Ctrl/⌘ + R: Toggle match/replace
                </Typography>
                <Typography variant='caption' display='block'>
                  Ctrl/⌘ + Enter: Focus test string
                </Typography>
                <Typography variant='caption' display='block'>
                  Ctrl/⌘ + 1-6: Quick patterns
                </Typography>
              </Box>
            }
          >
            <IconButton size='small'>
              <HelpIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Typography variant='body1' color='text.secondary' paragraph>
        Test and experiment with regular expressions in real-time. Enter your pattern and test
        string to see matches highlighted.
      </Typography>

      <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', lg: 'row' } }}>
        {/* Main Testing Area */}
        <Box sx={{ flex: showReference ? '2' : '1' }}>
          {/* Pattern Input */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Box
              sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}
            >
              <Typography variant='h6'>Regular Expression Pattern</Typography>

              {/* Pattern Status Indicators */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Chip
                  icon={<SpeedIcon />}
                  label={`${patternComplexity} complexity`}
                  size='small'
                  color={
                    patternComplexity === 'low'
                      ? 'success'
                      : patternComplexity === 'medium'
                        ? 'warning'
                        : 'error'
                  }
                />
                {isValid ? <CheckCircleIcon color='success' /> : <ErrorIcon color='error' />}
              </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <TextField
                fullWidth
                label='Enter your regex pattern'
                value={pattern}
                onChange={e => setPattern(e.target.value)}
                error={!isValid}
                helperText={error}
                placeholder='e.g., \d+, [a-z]+, \w+@\w+\.\w+'
                InputProps={{
                  style: { fontFamily: 'monospace' },
                  endAdornment: pattern && (
                    <IconButton onClick={() => setPattern('')} size='small'>
                      <ClearIcon />
                    </IconButton>
                  ),
                }}
              />

              <TextField
                label='Flags'
                value={flags}
                onChange={e => setFlags(e.target.value)}
                sx={{ width: 100 }}
                placeholder='g, i, m'
                InputProps={{
                  style: { fontFamily: 'monospace' },
                }}
              />
            </Box>

            {/* Pattern Explanation */}
            <Collapse in={showExplanation || !!pattern}>
              <Alert severity='info' sx={{ mb: 2 }}>
                <Typography variant='body2'>
                  <strong>Pattern explanation:</strong> {generatePatternExplanation(pattern)}
                </Typography>
              </Alert>
            </Collapse>

            {/* Sample Patterns */}
            <Box>
              <Typography variant='subtitle2' gutterBottom>
                Quick Patterns:
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {samplePatterns.map((sample, index) => (
                  <Tooltip key={index} title={sample.description}>
                    <Chip
                      label={sample.name}
                      onClick={() => handleSamplePatternClick(sample.pattern)}
                      clickable
                      size='small'
                    />
                  </Tooltip>
                ))}
              </Box>
            </Box>
          </Paper>

          {/* Test String Input */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant='h6' gutterBottom>
              Test String
            </Typography>

            <TextField
              fullWidth
              multiline
              rows={4}
              label='Enter text to test against your pattern'
              value={testString}
              onChange={e => setTestString(e.target.value)}
              placeholder='Enter text here...'
              sx={{ mb: 2 }}
            />

            {/* Replace String Input (only in replace mode) */}
            <Collapse in={testMode === 'replace'}>
              <TextField
                fullWidth
                label='Replacement string'
                value={replaceString}
                onChange={e => setReplaceString(e.target.value)}
                placeholder='Enter replacement text (use $1, $2 for capture groups)'
                sx={{ mb: 2, fontFamily: 'monospace' }}
                InputProps={{
                  style: { fontFamily: 'monospace' },
                }}
              />
            </Collapse>

            {/* Sample Strings */}
            <Box>
              <Typography variant='subtitle2' gutterBottom>
                Sample Text:
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {sampleStrings.map((sample, index) => (
                  <Button
                    key={index}
                    variant='outlined'
                    size='small'
                    onClick={() => handleSampleStringClick(sample)}
                    sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                  >
                    {sample.substring(0, 30)}...
                  </Button>
                ))}
              </Box>
            </Box>
          </Paper>

          {/* Results */}
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <Typography variant='h6'>
                {testMode === 'replace' ? 'Replace Results' : 'Match Results'}
              </Typography>
              {testMode === 'match' && matches.length > 0 && (
                <Chip
                  label={`${matches.length} match${matches.length !== 1 ? 'es' : ''}`}
                  color='success'
                />
              )}
            </Box>

            {pattern && testString && isValid && (
              <>
                {testMode === 'replace' ? (
                  /* Replace Results */
                  <Box sx={{ mb: 3 }}>
                    <Typography variant='subtitle2' gutterBottom>
                      Replacement result:
                    </Typography>
                    <Paper
                      variant='outlined'
                      sx={{
                        p: 2,
                        bgcolor: 'grey.50',
                        fontFamily: 'monospace',
                        fontSize: '0.875rem',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        border:
                          replaceResult !== testString ? '2px solid #4caf50' : '1px solid #e0e0e0',
                      }}
                    >
                      {replaceResult || testString}
                    </Paper>

                    {replaceResult && replaceResult !== testString && (
                      <Box sx={{ mt: 1 }}>
                        <Chip
                          icon={<CheckCircleIcon />}
                          label='Text was modified'
                          color='success'
                          size='small'
                        />
                        <IconButton
                          size='small'
                          onClick={() => copyToClipboard(replaceResult)}
                          sx={{ ml: 1 }}
                        >
                          <CopyIcon fontSize='small' />
                        </IconButton>
                      </Box>
                    )}
                  </Box>
                ) : (
                  /* Match Results */
                  <Box sx={{ mb: 3 }}>
                    <Typography variant='subtitle2' gutterBottom>
                      Text with matches highlighted:
                    </Typography>
                    <Paper
                      variant='outlined'
                      sx={{
                        p: 2,
                        bgcolor: 'grey.50',
                        fontFamily: 'monospace',
                        fontSize: '0.875rem',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                      }}
                    >
                      <div
                        dangerouslySetInnerHTML={{
                          __html: highlightMatches(testString, matches),
                        }}
                      />
                    </Paper>
                  </Box>
                )}

                {/* Match Details */}
                {matches.length > 0 && (
                  <Box>
                    <Typography variant='subtitle2' gutterBottom>
                      Match Details:
                    </Typography>
                    <List dense>
                      {matches.map((match, index) => (
                        <ListItem
                          key={index}
                          sx={{
                            bgcolor: 'action.hover',
                            mb: 1,
                            borderRadius: 1,
                            border: 1,
                            borderColor: 'divider',
                          }}
                        >
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography
                                  variant='body2'
                                  component='code'
                                  sx={{ bgcolor: 'warning.light', px: 1, borderRadius: 0.5 }}
                                >
                                  &ldquo;{match.match}&rdquo;
                                </Typography>
                                <Typography variant='caption'>at position {match.index}</Typography>
                                <IconButton
                                  size='small'
                                  onClick={() => copyToClipboard(match.match)}
                                >
                                  <CopyIcon fontSize='small' />
                                </IconButton>
                              </Box>
                            }
                            secondary={
                              match.groups &&
                              match.groups.length > 0 && (
                                <Typography variant='caption'>
                                  Groups: {match.groups.map(group => `"${group}"`).join(', ')}
                                </Typography>
                              )
                            }
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                )}

                {matches.length === 0 && (
                  <Alert severity='info'>
                    No matches found. Try adjusting your pattern or test string.
                  </Alert>
                )}
              </>
            )}

            {!pattern && (
              <Alert severity='info'>Enter a regular expression pattern to start testing.</Alert>
            )}
          </Paper>
        </Box>

        {/* Reference Sidebar */}
        {showReference && (
          <Box sx={{ flex: '1', minWidth: { lg: '300px' } }}>
            <Paper sx={{ p: 3 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mb: 2,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <HelpIcon color='primary' />
                  <Typography variant='h6'>Regex Reference</Typography>
                </Box>
                <IconButton
                  size='small'
                  onClick={() => setShowReference(false)}
                  sx={{ opacity: 0.6 }}
                >
                  <VisibilityOffIcon />
                </IconButton>
              </Box>

              {regexReference.map((category, categoryIndex) => (
                <Accordion key={categoryIndex} disableGutters>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant='subtitle1' fontWeight='bold'>
                      {category.category}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ pt: 0 }}>
                    <List dense>
                      {category.items.map((item, itemIndex) => (
                        <ListItem key={itemIndex} sx={{ pl: 0 }}>
                          <ListItemText
                            primary={
                              <Typography
                                component='code'
                                variant='body2'
                                sx={{
                                  fontFamily: 'monospace',
                                  bgcolor: 'grey.100',
                                  px: 0.5,
                                  py: 0.25,
                                  borderRadius: 0.5,
                                }}
                              >
                                {item.pattern}
                              </Typography>
                            }
                            secondary={item.description}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </AccordionDetails>
                </Accordion>
              ))}

              <Alert severity='info' sx={{ mt: 2 }}>
                <Typography variant='body2'>
                  <strong>Tip:</strong> Use the global flag (g) to find all matches,
                  case-insensitive flag (i) to ignore case, and multiline flag (m) for multi-line
                  strings.
                </Typography>
              </Alert>
            </Paper>
          </Box>
        )}
      </Box>
    </Container>
  );
};

export default RegexPlayground;
